/**
 * Speechify, for the speaking half.
 *
 * Speechify does text-to-speech and not the other way round, which is why the
 * settings grew two halves: it can be the family's voice while the browser or
 * Whisper does the listening. Its voices are markedly warmer than a browser's,
 * which matters more here than it would anywhere else — an assistant built to be
 * used without reading anything is an assistant you mostly experience as a voice.
 *
 *   POST https://api.speechify.ai/v1/audio/speech
 *   → { input, voice_id, audio_format, model?, language? }
 *   ← { audio_data: "<base64>", audio_format, billable_characters_count }
 *
 *   GET  https://api.speechify.ai/v1/voices?locale=en
 *   ← a list of voices; shapes vary, so it is read defensively below.
 *
 * **The audio comes back as base64 inside JSON**, not as bytes — so it is
 * decoded here rather than handed straight to an `<audio>` element.
 *
 * **A caution about calling it from a browser.** Speechify's own documentation
 * says an API key "should never be exposed in client-side code" and points at a
 * backend. That is right, and it is the same caution this app already gives for
 * every other key: it is an acceptable trade for a family testing on their own
 * devices, and the answer for anything beyond that is the `proxy` option, where
 * the key stays on a server you run. Whether the browser is allowed to call it
 * at all is up to Speechify's CORS policy, which is not documented — so a
 * failure here falls back to the browser's own voice rather than going silent.
 */
import type { Lang } from '../../domain/types';
import { logAssistant } from '../log';
import { bcp47, type TextToSpeech } from './types';

const API = 'https://api.speechify.ai/v1';

export interface SpeechifyOpts {
  apiKey: string;
  /** Override for a self-hosted or regional endpoint. */
  baseUrl?: string;
  /** The voice's id, as `voices()` returns it. */
  voiceId?: string;
  /** Left to the service unless set — except for Hebrew, which needs the multilingual model. */
  model?: string;
}

function base(o: SpeechifyOpts): string {
  return (o.baseUrl?.trim() || API).replace(/\/+$/, '');
}

/** Hebrew is not the English model's job. */
function modelFor(o: SpeechifyOpts, lang: Lang): string | undefined {
  if (o.model?.trim()) return o.model.trim();
  return lang === 'he' ? 'simba-multilingual' : undefined;
}

function base64ToBlob(b64: string, type: string): Blob {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type });
}

export function createSpeechifyTts(o: SpeechifyOpts): TextToSpeech {
  let playing: HTMLAudioElement | null = null;
  return {
    id: 'speechify',
    available: () => !!o.apiKey.trim(),

    /**
     * The family's choice of voice, read from the service.
     *
     * The response shape is not pinned down in the documentation and has moved
     * before, so every field is read defensively: a list may arrive bare or
     * wrapped, and a voice may name itself `id` or `voice_id`. Anything that
     * cannot be understood is skipped rather than throwing — a voice list that
     * fails to parse should cost you the dropdown, not the assistant.
     */
    async voices() {
      if (!o.apiKey.trim()) return [];
      let data: unknown;
      try {
        const res = await fetch(`${base(o)}/voices`, { headers: { authorization: `Bearer ${o.apiKey.trim()}` } });
        if (!res.ok) {
          logAssistant('voice', 'fail', `Speechify refused the voice list (HTTP ${res.status})`, await res.text().catch(() => ''));
          return [];
        }
        data = await res.json();
      } catch (e) {
        // Almost always CORS or no network, and until now it cost the dropdown
        // in complete silence — which looks exactly like a service with no
        // voices in it.
        logAssistant('voice', 'fail', 'Speechify could not be reached for the voice list', e);
        return [];
      }
      const list = Array.isArray(data)
        ? data
        : Array.isArray((data as { voices?: unknown[] })?.voices)
          ? (data as { voices: unknown[] }).voices
          : [];
      const out: Array<{ id: string; name: string }> = [];
      for (const raw of list) {
        const v = raw as Record<string, unknown>;
        const id = typeof v.id === 'string' ? v.id : typeof v.voice_id === 'string' ? v.voice_id : null;
        if (!id) continue;
        const name = typeof v.display_name === 'string' ? v.display_name : typeof v.name === 'string' ? v.name : id;
        const locale = typeof v.locale === 'string' ? v.locale : typeof v.language === 'string' ? v.language : '';
        const gender = typeof v.gender === 'string' ? v.gender : '';
        const detail = [locale, gender].filter(Boolean).join(' · ');
        out.push({ id, name: detail ? `${name} (${detail})` : name });
      }
      return out.sort((a, b) => a.name.localeCompare(b.name));
    },

    async speak(text: string, lang: Lang, voiceName?: string) {
      if (!text.trim()) return;
      this.stop();
      const model = modelFor(o, lang);
      const res = await fetch(`${base(o)}/audio/speech`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${o.apiKey.trim()}` },
        body: JSON.stringify({
          input: text,
          voice_id: voiceName || o.voiceId || 'george',
          audio_format: 'mp3',
          language: bcp47(lang),
          ...(model ? { model } : {}),
        }),
      });
      // Thrown rather than swallowed: the layer above falls back to the
      // browser's own voice, so a blocked call means a plainer voice and not a
      // silent assistant. The body comes with it, because "speechify 401" does
      // not tell anybody whether the key is wrong, expired or out of characters.
      if (!res.ok) throw new Error(`speechify ${res.status}: ${(await res.text().catch(() => '')).slice(0, 200)}`);
      const data = (await res.json()) as { audio_data?: string; audio_format?: string };
      if (!data.audio_data) throw new Error('speechify: the reply had no audio in it');

      const url = URL.createObjectURL(base64ToBlob(data.audio_data, data.audio_format === 'wav' ? 'audio/wav' : 'audio/mpeg'));
      const audio = new Audio(url);
      playing = audio;
      try {
        await new Promise<void>((resolve, reject) => {
          audio.onended = () => resolve();
          audio.onerror = () => reject(new Error('speechify: the audio came back but would not play'));
          /*
           * The failure nobody expects: the browser refusing to play at all.
           *
           * Autoplay rules want a recent user gesture, and an answer arrives
           * several seconds after the microphone was tapped — long enough, in
           * some browsers, for the tap to stop counting. It rejects with
           * `NotAllowedError` and every other sign says the voice worked. Named
           * here so the log says which of the two it was.
           */
          void audio.play().catch((e: unknown) => {
            const name = e instanceof Error ? e.name : '';
            reject(name === 'NotAllowedError' ? new Error('speechify: the browser blocked the sound (autoplay) — it needs a tap first') : e);
          });
        });
      } finally {
        URL.revokeObjectURL(url);
        if (playing === audio) playing = null;
      }
    },

    stop() {
      playing?.pause();
      playing = null;
    },
  };
}
